import React, { useState, useEffect } from "react";
import { PlanMock } from "./AccountSettingsModal";
import { UserSubscription } from "../../../types/subscription";
import { api } from "../../../utils/api";
import { API_ENDPOINTS } from "../../../config";
import { showToast } from "../../../utils/toastManager";
import "../../../styles/account-settings.css";

const FREE_PLAN_CODES = ['FREE_PLAN', 'TRIAL_PLAN'];

interface PaymentItem {
  payment_id: string | null;
  amount: number | null;
  currency: string;
  created_at: string | null;
  status: string | null;
}

type Props = {
  plan: PlanMock;
  userSubscription?: UserSubscription | null;
};

export const PlanUsageCard: React.FC<Props> = ({ plan, userSubscription }) => {
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const isPaidPlan = userSubscription?.plan?.code
    ? !FREE_PLAN_CODES.includes(userSubscription.plan.code)
    : false;
  const shouldShowUpgrade = userSubscription?.plan?.code
    ? FREE_PLAN_CODES.includes(userSubscription.plan.code)
    : false;
  const shouldShowCancel = isPaidPlan && userSubscription?.status === 'active';

  useEffect(() => {
    if (!isPaidPlan) return;
    const fetchPayments = async () => {
      try {
        setLoadingPayments(true);
        const data = await api.get<PaymentItem[]>(API_ENDPOINTS.payments.getPaymentHistory);
        setPayments(data ?? []);
      } catch {
      } finally {
        setLoadingPayments(false);
      }
    };
    fetchPayments();
  }, [isPaidPlan]);

  const handleCancelSubscription = async () => {
    if (!confirmCancel) {
      setConfirmCancel(true);
      return;
    }
    try {
      setCancelling(true);
      await api.post(API_ENDPOINTS.subscriptions.cancelSubscription);
      showToast('Subscription will be cancelled at the end of the billing period.', 'success');
      setConfirmCancel(false);
    } catch (err: any) {
      showToast(err.message || 'Failed to cancel subscription. Please try again.', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const handleViewInvoice = async (paymentId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(API_ENDPOINTS.payments.getPaymentInvoice(paymentId), {
        headers: { Authorization: `Bearer ${token ?? ''}` },
      });
      if (!response.ok) throw new Error('Failed to load invoice');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      showToast('Failed to open invoice. Please try again.', 'error');
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const formatLimit = (limit: number) => limit === -1 ? 'Unlimited' : limit.toString();

  const formatAmount = (amount: number | null, currency: string) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const getUsageDisplay = () => {
    if (!userSubscription?.plan) {
      return {
        jobBoards: 'Unlimited',
        jobs: 'Unlimited',
        contacts: 'Unlimited',
        documents: 'Unlimited',
        resumes: 'Unlimited',
        aiGenerations: 'Unlimited',
        aiLabel: 'AI Generations',
      };
    }

    const p = userSubscription.plan;
    const u = userSubscription.usage;
    const aiLabel = p.is_ai_tokens_limit ? 'Subscription tokens' : 'AI Generations';
    const aiLimit = p.is_ai_tokens_limit ? p.subscription_ai_tokens : p.ai_tools_calls_limit;
    const aiUsed = p.is_ai_tokens_limit
      ? (u?.ai_tokens_subscription ?? 0)
      : (u?.ai_tools_calls_usage ?? 0);

    return {
      jobBoards: formatLimit(p.job_board_limit),
      jobBoardsUsed: u?.job_board_usage ?? 0,
      jobs: formatLimit(p.job_position_limit),
      jobsUsed: u?.job_position_usage ?? 0,
      contacts: formatLimit(p.contacts_limit),
      contactsUsed: u?.contacts_usage ?? 0,
      documents: formatLimit(p.documents_limit),
      documentsUsed: u?.documents_usage ?? 0,
      resumes: formatLimit(p.resumes_limit),
      resumesUsed: u?.resumes_usage ?? 0,
      aiGenerations: formatLimit(aiLimit),
      aiGenerationsUsed: aiUsed,
      aiLabel,
    };
  };

  const usage = getUsageDisplay();

  return (
    <div className="as-Section">
      <div className="as-SectionTitle">Plan Info</div>
      <div className="as-Divider" />

      <div className="as-Island">
        <div className="as-IslandLeft">
          <div className="as-PlanName">{plan.name}</div>
          {userSubscription?.expires_at && (
            <div className="as-PlanPrice">
              Expires at: {formatDate(userSubscription.expires_at)}
            </div>
          )}
        </div>

        <div className="as-VRule" />

        <div className="as-IslandRight">
          <div className="as-PlanDesc">{plan.description}</div>
          {shouldShowUpgrade && (
            <button className="as-UpgradeBtn">Upgrade</button>
          )}
          {userSubscription?.status === 'cancelling' && (
            <div className="as-CancelNotice">
              Subscription is scheduled for cancellation. You'll keep access until{' '}
              <strong>{formatDate(userSubscription.expires_at)}</strong>.
            </div>
          )}
          {shouldShowCancel && (
            <div className="as-CancelArea">
              {confirmCancel ? (
                <>
                  <p className="as-CancelConfirmText">
                    Are you sure? You'll keep access until the end of the current billing period.
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="as-Btn as-BtnDanger"
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                    >
                      {cancelling ? 'Cancelling...' : 'Yes, cancel'}
                    </button>
                    <button
                      className="as-Btn"
                      onClick={() => setConfirmCancel(false)}
                      disabled={cancelling}
                    >
                      Keep subscription
                    </button>
                  </div>
                </>
              ) : (
                <button
                  className="as-Btn as-BtnDangerOutline"
                  onClick={handleCancelSubscription}
                >
                  Cancel subscription
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="as-Note">
        Note: Any discounts for coupons applied during checkout will not be reflected in the price you see here. They will, however, be reflected in your actual monthly charges.
      </div>

      <div className="as-SectionTitle mt16">Usage {plan.name}</div>
      <div className="as-Divider" />
      <div className="as-UsageList">
        <div className="as-UsageItem">
          <b>Job Boards:</b> {usage.jobBoardsUsed} / {usage.jobBoards}
        </div>
        <div className="as-UsageItem">
          <b>Jobs:</b> {usage.jobsUsed} / {usage.jobs}
        </div>
        <div className="as-UsageItem">
          <b>Contacts:</b> {usage.contactsUsed} / {usage.contacts}
        </div>
        <div className="as-UsageItem">
          <b>Documents:</b> {usage.documentsUsed} / {usage.documents}
        </div>
        <div className="as-UsageItem">
          <b>Resumes:</b> {usage.resumesUsed} / {usage.resumes}
        </div>
        <div className="as-UsageItem">
          <b>{usage.aiLabel}:</b> {usage.aiGenerationsUsed} / {usage.aiGenerations}
        </div>
      </div>

      {isPaidPlan && (
        <>
          <div className="as-SectionTitle mt16">Payment History</div>
          <div className="as-Divider" />
          {loadingPayments ? (
            <div className="as-PaymentEmpty">Loading payments...</div>
          ) : payments.length === 0 ? (
            <div className="as-PaymentEmpty">No payments found.</div>
          ) : (
            <div className="as-PaymentScroll">
              <div className="as-PaymentList">
                {payments.map((p, i) => (
                  <div key={p.payment_id ?? i} className="as-PaymentItem">
                    <span className="as-PaymentDate">{formatDate(p.created_at)}</span>
                    <span className="as-PaymentAmount">{formatAmount(p.amount, p.currency)}</span>
                    <div className="as-PaymentStatusWrap">
                      <span className={`as-PaymentStatus as-PaymentStatus--${p.status ?? 'unknown'}`}>
                        {p.status ?? 'unknown'}
                      </span>
                    </div>
                    {p.payment_id ? (
                      <a
                        className="as-InvoiceLink"
                        href="#"
                        onClick={(e) => { e.preventDefault(); handleViewInvoice(p.payment_id!); }}
                      >
                        View invoice →
                      </a>
                    ) : (
                      <span />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PlanUsageCard;
